import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Card } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, CalendarDays, LineChart, TrendingUp } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type KpiClientDTO, type KpiSales, type KpiStoreDTO } from "../api";
import { money, TwoLine } from "../components/cells";
import { AccessDenied, useAllowed } from "../components/GuardedPage";
import { LogicView } from "../views";

/**
 * The KPI dashboard.
 *
 * Four period panels over two ranked grids, exactly as `HomeView` laid it out:
 * last 7 days, the calendar month, the quarter and YTD, each with a
 * versus-previous-period delta where the backend supplies one (the 7-day panel
 * has no comparison figure, so it shows none).
 */
export function HomePage() {
  const { t } = useTranslation();
  const api = useApi();
  const allowed = useAllowed(LogicView.HOME);

  const totals = useQuery({
    queryKey: ["kpi", "total"],
    queryFn: () => api.kpiStores.getTotalSales(),
    enabled: allowed,
  });

  const stores = useQuery({
    queryKey: ["kpi", "store", "latest"],
    queryFn: () => api.kpiStores.getAllLatest(),
    enabled: allowed,
  });

  const clientKpis = useQuery({
    queryKey: ["kpi", "client", "latest"],
    queryFn: () => api.kpiClients.getAllLatest(),
    enabled: allowed,
  });

  const storeColumns = useMemo(() => kpiColumns<KpiStoreDTO>((row) => row.store?.name ?? "", t), [t]);
  const clientColumns = useMemo(
    () => kpiColumns<KpiClientDTO>((row) => row.client?.name ?? "", t),
    [t],
  );

  if (!allowed) return <AccessDenied title={t("navigation.item.dashboard", "Dashboard")} />;

  const kpi = totals.data;
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3) + 1;

  return (
    <>
      <PageHeader title={t("navigation.item.dashboard", "Dashboard")} />

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4 lg:p-6">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <KpiPanel
            icon={<TrendingUp className="size-4" />}
            tone="ok"
            title={t("home.kpi.panel.7days", "Last 7 Days")}
            units={kpi?.sales_units_7_days}
            usd={kpi?.sales_usd_7_days}
          />
          <KpiPanel
            icon={<CalendarDays className="size-4" />}
            tone="accent"
            title={now.toLocaleString(undefined, { month: "long" })}
            units={kpi?.sales_units_month}
            usd={kpi?.sales_usd_month}
            prevUnits={kpi?.sales_units_last_month}
            prevUsd={kpi?.sales_usd_last_month}
          />
          <KpiPanel
            icon={<BarChart3 className="size-4" />}
            tone="neutral"
            title={`Q${quarter} ${now.getFullYear()}`}
            units={kpi?.sales_units_quarter}
            usd={kpi?.sales_usd_quarter}
            prevUnits={kpi?.sales_units_last_quarter}
            prevUsd={kpi?.sales_usd_last_quarter}
          />
          <KpiPanel
            icon={<LineChart className="size-4" />}
            tone="risk"
            title={`${now.getFullYear()} ${t("home.kpi.panel.ytd", "YTD")}`}
            units={kpi?.sales_units_ytd}
            usd={kpi?.sales_usd_ytd}
            prevUnits={kpi?.sales_units_last_year}
            prevUsd={kpi?.sales_usd_last_year}
          />
        </div>

        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-2">
          <KpiSection
            title={t("home.kpi.section.stores", "Store Performance")}
            accent="var(--accent)"
          >
            <DataTable<KpiStoreDTO>
              data={stores.data ?? []}
              columns={storeColumns}
              getRowId={(row) => String(row.id)}
              loading={stores.isLoading}
              initialSorting={[{ id: "ytd", desc: true }]}
            />
          </KpiSection>

          <KpiSection
            title={t("home.kpi.section.clients", "Client Performance")}
            accent="var(--ok)"
          >
            <DataTable<KpiClientDTO>
              data={clientKpis.data ?? []}
              columns={clientColumns}
              getRowId={(row) => String(row.id)}
              loading={clientKpis.isLoading}
              initialSorting={[{ id: "ytd", desc: true }]}
            />
          </KpiSection>
        </div>
      </div>
    </>
  );
}

function KpiSection({
  title,
  accent,
  children,
}: {
  title: string;
  accent: string;
  children: ReactNode;
}) {
  return (
    <section className="flex min-h-0 flex-col">
      <h2
        className="border-b-2 pb-1 text-[15px] font-bold tracking-wide"
        style={{ color: accent, borderColor: accent }}
      >
        {title}
      </h2>
      <div className="mt-2 flex min-h-72 flex-1 flex-col overflow-hidden rounded-lg border border-line bg-surface">
        {children}
      </div>
    </section>
  );
}

const TONES = {
  ok: "bg-ok-soft text-ok",
  accent: "bg-accent-soft text-accent",
  neutral: "bg-surface-sunk text-ink-muted",
  risk: "bg-risk-soft text-risk",
} as const;

function KpiPanel({
  icon,
  tone,
  title,
  units,
  usd,
  prevUnits,
  prevUsd,
}: {
  icon: ReactNode;
  tone: keyof typeof TONES;
  title: string;
  units: number | undefined;
  usd: number | undefined;
  prevUnits?: number | undefined;
  prevUsd?: number | undefined;
}) {
  const { t } = useTranslation();
  const hasComparison = prevUnits !== undefined && prevUsd !== undefined;
  const deltaUnits = hasComparison ? (units ?? 0) - prevUnits : 0;
  const deltaUsd = hasComparison ? (usd ?? 0) - prevUsd : 0;
  const positive = deltaUnits >= 0;

  return (
    <Card className="p-3">
      <div className="flex items-start gap-3">
        <span className={`inline-flex size-8 items-center justify-center rounded-md ${TONES[tone]}`}>
          {icon}
        </span>
        <div className="min-w-0 flex-1 text-right">
          <p className="truncate text-[12px] font-medium text-ink-muted">{title}</p>
          <p className="text-lg font-bold tabular-nums text-ink">
            {units ?? "—"} <span className="text-[12px] font-normal text-ink-muted">
              {t("home.kpi.units", "units")}
            </span>
          </p>
          <p className="text-[13px] tabular-nums text-ink-muted">
            {usd === undefined ? "—" : money(usd)} {t("home.kpi.usd", "USD")}
          </p>
        </div>
      </div>

      {hasComparison && (
        <div className="mt-2 border-t border-line pt-1.5 text-right text-[11px]">
          <span className="text-ink-faint">{t("home.kpi.vs-last", "vs last")} </span>
          <span className={positive ? "font-semibold text-ok" : "font-semibold text-risk"}>
            {positive ? "▲ +" : "▼ "}
            {deltaUnits} {t("home.kpi.units", "units")} • {deltaUsd >= 0 ? "+" : "−"}
            {money(Math.abs(deltaUsd))} {t("home.kpi.usd", "USD")}
          </span>
        </div>
      )}
    </Card>
  );
}

/** The four period cells are identical for stores and clients. */
function kpiColumns<T extends KpiSales>(
  name: (row: T) => string,
  t: (key: string, fallback: string) => string,
) {
  const c = defineColumns<T>();
  const cell = (units: number, usd: number) => (
    <TwoLine primary={`${units} ${t("home.kpi.units", "units")}`} secondary={`${money(usd)} USD`} />
  );

  return [
    c.text("name", name, { headerKey: "column.name", priority: 1 }),
    c.custom("7days", (row) => row.sales_units_7_days, (row) => cell(row.sales_units_7_days, row.sales_usd_7_days), {
      headerKey: "column.kpi.7days",
      filter: "none",
      align: "right",
      priority: 2,
    }),
    c.custom("month", (row) => row.sales_units_month, (row) => cell(row.sales_units_month, row.sales_usd_month), {
      headerKey: "column.kpi.month",
      filter: "none",
      align: "right",
      priority: 3,
    }),
    c.custom("quarter", (row) => row.sales_units_quarter, (row) => cell(row.sales_units_quarter, row.sales_usd_quarter), {
      headerKey: "column.kpi.quarter",
      filter: "none",
      align: "right",
      priority: 4,
    }),
    c.custom("ytd", (row) => row.sales_units_ytd, (row) => cell(row.sales_units_ytd, row.sales_usd_ytd), {
      headerKey: "column.kpi.ytd",
      filter: "none",
      align: "right",
      priority: 3,
    }),
  ];
}
